import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Search, X, BookOpen, Book, HeartHandshake, Newspaper } from "lucide-react";
import { understandingTopics, glossary, supportDirectory } from "../lib/content";
import { fetchJournalList } from "../lib/api";

const RECORDS_ENTRIES = [
  {
    kind: "guide",
    id: "records",
    title: "Getting your records",
    summary:
      "Subject access, freedom of information, legal privilege, and how to complain to the ICO.",
    to: "/understanding/records",
    haystack:
      "records subject access freedom of information foi sar privilege ico information commissioner data protection cafcass",
  },
  {
    kind: "guide",
    id: "records-cheat",
    title: "Records — one-page cheat sheet",
    summary: "Printable A4 summary you can take into meetings.",
    to: "/understanding/records/cheat-sheet",
    haystack: "records cheat sheet print pdf a4 summary sar foi",
  },
  {
    kind: "guide",
    id: "records-templates",
    title: "SAR & FOI email templates",
    summary:
      "Ready-to-send subject access and freedom of information emails, pre-filled with your name, address and local authority.",
    to: "/understanding/records/templates",
    haystack:
      "sar foi templates subject access request freedom of information email letter draft copy paste local authority ico deadline tracker reminder calendar countdown chase",
  },
  {
    kind: "guide",
    id: "records-chronology",
    title: "Chronology builder",
    summary:
      "A tiny printable table you can fill in day by day, so that when it matters you have a dated record of every meeting, letter and call.",
    to: "/understanding/records/chronology",
    haystack:
      "chronology diary log timeline record meetings letters calls dates notes evidence court",
  },
  {
    kind: "guide",
    id: "meeting-prep",
    title: "Before your meeting — prep checklist",
    summary:
      "A printable one-page checklist: what to bring, what to ask, and what to write down after a child protection meeting.",
    to: "/understanding/meeting-prep",
    haystack:
      "meeting prep preparation checklist conference core group child in need cin plo bring ask questions minutes notes supporter advocate print pdf",
  },
  {
    kind: "guide",
    id: "question-cards",
    title: "Pocket question cards",
    summary:
      "Eight wallet-size cut-out cards, each with one strong question to ask in a meeting — and why it works.",
    to: "/understanding/meeting-prep/cards",
    haystack:
      "question cards pocket wallet cut out print questions ask meeting conference advocacy strong",
  },
  {
    kind: "guide",
    id: "meeting-debrief",
    title: "Meeting debrief — after the meeting",
    summary:
      "Six short questions right after a meeting — who was there, what was decided, what each side agreed — added straight into your chronology.",
    to: "/understanding/meeting-prep/debrief",
    haystack:
      "debrief after meeting notes minutes decided agreed disagreed chronology record same day",
  },
  {
    kind: "guide",
    id: "records-complaint",
    title: "Missed deadline — chase & ICO complaint letters",
    summary:
      "If your SAR or FOI is overdue: a ready-to-send chase letter and an ICO complaint, built from your saved details and dates.",
    to: "/understanding/records/complaint",
    haystack:
      "complaint chase letter overdue missed deadline ico information commissioner internal review sar foi escalate no reply",
  },
  {
    kind: "guide",
    id: "about",
    title: "About this site",
    summary:
      "Who runs it, why it exists, what it does and does not do, and how it is written and checked.",
    to: "/about",
    haystack:
      "about site who runs independent one person no funding no organisation charity law firm campaign group insurance semi retired evidence primary sources england wales coverage",
  },
];

const ICONS = {
  guide: BookOpen,
  glossary: Book,
  support: HeartHandshake,
  journal: Newspaper,
};

const LABELS = {
  guide: "Understanding the system",
  glossary: "Glossary",
  support: "Find support",
  journal: "Journal",
};

function buildIndex(journal) {
  const out = [];

  understandingTopics.forEach((t) => {
    out.push({
      kind: "guide",
      id: t.id,
      title: t.title,
      summary: t.summary,
      to: `/understanding#${t.id}`,
      haystack: `${t.title} ${t.summary} ${t.body.join(" ")}`.toLowerCase(),
    });
  });
  RECORDS_ENTRIES.forEach((e) =>
    out.push({ ...e, haystack: e.haystack.toLowerCase() })
  );

  glossary.forEach((g) => {
    out.push({
      kind: "glossary",
      id: g.term,
      title: g.term,
      summary: g.definition,
      to: `/glossary?q=${encodeURIComponent(g.term)}`,
      haystack: `${g.term} ${g.definition}`.toLowerCase(),
    });
  });

  supportDirectory.forEach((s) => {
    out.push({
      kind: "support",
      id: s.name,
      title: s.name,
      summary: `${s.type} · ${s.region} — ${s.description}`,
      to: "/support",
      haystack: `${s.name} ${s.type} ${s.region} ${s.description}`.toLowerCase(),
    });
  });

  (journal || []).forEach((p) => {
    out.push({
      kind: "journal",
      id: p.slug,
      title: p.title,
      summary: p.excerpt || p.tag || "",
      to: `/journal/${p.slug}`,
      haystack: `${p.title} ${p.excerpt || ""} ${p.tag || ""} ${p.body || ""}`.toLowerCase(),
    });
  });

  return out;
}

function scoreItem(item, q) {
  const title = item.title.toLowerCase();
  if (title === q) return 100;
  if (title.startsWith(q)) return 60;
  if (title.includes(q)) return 40;
  if (item.haystack.includes(q)) return 15;
  return 0;
}

export default function SearchModal({ open, onClose }) {
  const [q, setQ] = useState("");
  const [journal, setJournal] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Load journal once, on first open
  useEffect(() => {
    if (open && !loaded) {
      fetchJournalList()
        .then((posts) => setJournal(Array.isArray(posts) ? posts : []))
        .catch(() => setJournal([]))
        .finally(() => setLoaded(true));
    }
  }, [open, loaded]);

  // Focus + reset when opened; lock body scroll while open
  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 30);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const index = useMemo(() => buildIndex(journal), [journal]);

  const grouped = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return null;
    const scored = index
      .map((item) => ({ item, score: scoreItem(item, query) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);
    const out = { guide: [], glossary: [], support: [], journal: [] };
    scored.forEach(({ item }) => {
      if (out[item.kind] && out[item.kind].length < 6) out[item.kind].push(item);
    });
    return out;
  }, [q, index]);

  const total =
    grouped
      ? grouped.guide.length +
        grouped.glossary.length +
        grouped.support.length +
        grouped.journal.length
      : 0;

  const go = (to) => {
    onClose();
    navigate(to);
  };

  if (!open) return null;

  return createPortal(
    <div
      data-testid="search-modal"
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-label="Search the site"
    >
      <button
        type="button"
        aria-label="Close search"
        onClick={onClose}
        data-testid="search-modal-backdrop"
        className="absolute inset-0 w-full h-full bg-ink/75 backdrop-blur-sm cursor-default"
      />
      <div className="absolute inset-0 overflow-y-auto pointer-events-none">
        <div className="min-h-full flex items-start justify-center px-4 py-16 md:py-24">
          <div className="pointer-events-auto w-full max-w-2xl bg-linen border border-rule shadow-2xl">
            {/* Search input */}
            <div className="flex items-center gap-3 border-b border-rule px-5 py-4">
              <Search size={18} strokeWidth={1.5} className="text-ink-muted flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search guides, glossary, journal…"
                data-testid="search-modal-input"
                className="flex-1 bg-transparent text-base md:text-lg outline-none text-ink placeholder:text-ink-muted/60"
              />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="search-modal-close"
                className="text-ink-muted hover:text-ink p-1"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[65vh] overflow-y-auto" data-testid="search-modal-results">
          {!grouped && (
            <div className="p-6 text-sm text-ink-muted">
              <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold mb-2">
                Suggestions
              </div>
              <ul className="space-y-1.5">
                {["section 47", "child protection conference", "care order", "records", "kinship"].map(
                  (s) => (
                    <li key={s}>
                      <button
                        type="button"
                        onClick={() => setQ(s)}
                        className="text-river hover:text-ember underline underline-offset-4"
                      >
                        {s}
                      </button>
                    </li>
                  )
                )}
              </ul>
              <p className="mt-4 text-xs text-ink-muted/80">
                Press <kbd className="border border-rule px-1 py-0.5 text-[10px]">Esc</kbd> to close.
              </p>
            </div>
          )}

          {grouped && total === 0 && (
            <div className="p-8 text-center text-ink-muted" data-testid="search-modal-empty">
              No matches for &ldquo;{q}&rdquo;.
              <div className="mt-2 text-xs">Try a shorter word — for example &ldquo;order&rdquo; instead of &ldquo;placement order&rdquo;.</div>
            </div>
          )}

          {grouped && total > 0 && (
            <div className="divide-y divide-rule/70">
              {(["guide", "glossary", "support", "journal"]).map((kind) => {
                const items = grouped[kind];
                if (!items.length) return null;
                const Icon = ICONS[kind];
                return (
                  <section key={kind} data-testid={`search-group-${kind}`} className="py-3">
                    <div className="px-5 pb-2 text-[10px] uppercase tracking-[0.22em] text-ember font-accent font-semibold flex items-center gap-2">
                      <Icon size={12} strokeWidth={1.75} />
                      {LABELS[kind]}
                    </div>
                    <ul>
                      {items.map((item) => (
                        <li key={`${kind}-${item.id}`}>
                          <button
                            type="button"
                            onClick={() => go(item.to)}
                            data-testid={`search-result-${kind}-${String(item.id).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            className="w-full text-left px-5 py-2.5 hover:bg-white transition-colors"
                          >
                            <div className="font-serif text-ink text-base leading-tight">
                              {item.title}
                            </div>
                            <div className="text-xs text-ink-muted mt-1 leading-snug line-clamp-2">
                              {item.summary}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
